import { CommonModule } from '@angular/common';
import { Component, OnInit, WritableSignal, signal, input, inject, InputSignal, computed } from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { RouterModule } from '@angular/router';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SignalsStoreService } from '../../signals-store.service';
import { CartService } from '../../cart.service';
import { FormsModule } from '@angular/forms';
import { OrderService } from '../../order.service';
import { NumberRestrictionDirective } from '../../directives/number-restriction.directive';
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { UpdateOrderParams } from '../../types/order.type';
import { setUpBundleChangesLog } from '../../common/bundle.utils';
import { updateFirebaseOrderAndSubTrigger } from '../../common/order.utils';
import { finalize, tap } from 'rxjs';
import { ProductType } from '../../types/common.enums';
import { FirebaseCrudService } from '../../firebase-crud.service';

@Component({
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    NumberRestrictionDirective,
    NgbTooltip
  ],
  selector: 'app-product-cart-preview',
  templateUrl: './product-cart-preview.component.html',
  styleUrl: './product-cart-preview.component.scss',
})

export class ProductCartPreview implements OnInit {
  signalsStoreService = inject(SignalsStoreService)
  orderService = inject(OrderService)
  #firebaseCrudService = inject(FirebaseCrudService);

  isInputValid!: boolean;
  previousValue!: number;
  selectedQuantity: WritableSignal<any> = signal(1);
  newQuantity: WritableSignal<any> = signal(1);

  product: any = input(null, {
    alias: 'product',
    transform: (product: any) => {
      this.previousValue = product.quantity;
      this.newQuantity.set(product.quantity)
      return product;
    }
  })

  productKey: any = input(null, {
    alias: 'productKey'
  })

  allowModification: InputSignal<boolean> = input(true);

  isJustAddedProductNotification: InputSignal<boolean> = input(false, {
    alias: 'isJustAddedProductNotification'
  });

  totalPrice = computed(() => this.#setupProductTotalPrice());


  ngOnInit(): void {
    this.isInputValid = true;
  }

  private validateInput(inputValue: number): void {
    const regex = /^\d+$/;
    this.isInputValid = regex.test(inputValue.toString());
  }

  onKeyUp(event: any): void {
    const { value } = event?.target
    this.validateInput(+value);
    if (!this.isInputValid)
      return

    this.newQuantity.set(+value)
  }

  updateQuantity() {
    if (!this.newQuantity() || this.newQuantity() === this.previousValue) return;
    this.#updateProduct();
  }

  removeCartProduct() {
    const odooOrder = this.orderService.order();

    if (!odooOrder?.id)
      return;

    this.orderService
      .deleteProductOrder(+odooOrder.id, this.product().lineId)
      .pipe(finalize(() => updateFirebaseOrderAndSubTrigger(this.signalsStoreService, this.#firebaseCrudService)))
      .subscribe()
  }

  handleMinus() {
    const current = this.newQuantity();
    if (current <= 1) return;
    this.newQuantity.update((current) => current - 1);
    this.updateQuantity();
  }

  handlePlus() {
    this.newQuantity.update((current) => current + 1);
    this.updateQuantity();
  }

  handleImageError(event: Event) {
    (event.target as HTMLImageElement).src = 'assets/images/product-placeholder.webp';
  }

  #updateProduct() {

    const copy = this.product();
    copy.quantity = this.newQuantity();
    copy.bundleQuantityChanged = true;

    const obj: UpdateOrderParams = {
      orderId: this.orderService.order()?.id,
      product: { ...copy, variantId: copy.variant.id, packageId: copy.package?.id || null } as any,
      type: this.productKey(),
      bundleChangesLog: this.product()?.isBundle ? setUpBundleChangesLog(this.product(), this.newQuantity()) : undefined
    };

    this.orderService
      .updateOrder(obj, this.productKey() === ProductType.subscription)
      .pipe(tap(() => updateFirebaseOrderAndSubTrigger(this.signalsStoreService, this.#firebaseCrudService)))
      .subscribe();
  }

  #setupProductTotalPrice() {
    const p = this.product();
    if (!p.isBundle) return p.totalPrice;

    const totalPrice = this.orderService.calculateProductTotalPrice(p);
    return totalPrice;
  }
}
